import React from 'react';
import { Check, LucideIcon } from 'lucide-react';
import { BD } from '../bookDemoTheme';

interface SelectionCardProps {
  title: string;
  description?: string;
  icon: LucideIcon;
  selected?: boolean;
  onClick: () => void;
  /** `challenge` lays the icon beside the copy on ≥sm; `grid` stacks it above. */
  variant?: 'grid' | 'challenge';
}

const SelectionCard: React.FC<SelectionCardProps> = ({
  title,
  description,
  icon: Icon,
  selected = false,
  onClick,
  variant = 'grid',
}) => {
  const shell = `${BD.cardBg} ${BD.cardShadow} border w-full text-left rounded-xl p-3 sm:p-4 transition duration-150 ${
    selected ? `${BD.selectedBorder} ${BD.selectedBg}` : `border-transparent ${BD.hoverBorder}`
  }`;
  const iconTile = `rounded-lg flex items-center justify-center border ${
    selected ? BD.selectedIconBg : BD.idleIconBg
  }`;

  if (variant === 'challenge') {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`${shell} relative flex flex-col sm:flex-row items-start gap-3`}
      >
        <div className={`${iconTile} w-10 sm:w-12 h-10 sm:h-12 flex-shrink-0`}>
          <Icon size={24} strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className={`text-[16px] sm:text-[20px] leading-5 mb-2 font-semibold ${BD.textPrimary}`}>{title}</p>
          {description && (
            <p
              className={`text-[12px] sm:text-[14px] leading-[18px] sm:leading-[21px] font-medium sm:pr-8 ${
                selected ? BD.textSecondary : BD.textMuted
              }`}
            >
              {description}
            </p>
          )}
        </div>
      </button>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-pressed={selected} className={`${shell} relative flex flex-col`}>
      {selected && (
        <span
          className={`absolute top-3 sm:top-4 right-3 sm:right-4 w-5 h-5 rounded-full flex items-center justify-center ${BD.accentBgDeep} ${BD.textOnAccent}`}
        >
          <Check size={16} strokeWidth={1.5} />
        </span>
      )}
      <div className={`${iconTile} w-10 sm:w-12 h-10 sm:h-12 mb-2`}>
        <Icon size={24} strokeWidth={1.5} />
      </div>
      <p className={`text-[16px] sm:text-[18px] leading-6 font-semibold mb-1 ${BD.textPrimary}`}>{title}</p>
      {description && (
        <p className={`text-[12px] sm:text-[14px] leading-[18px] sm:leading-6 font-normal ${BD.textMuted}`}>
          {description}
        </p>
      )}
    </button>
  );
};

export default SelectionCard;
